<template>
  <div class="guide-branding">
    <h2>Branding</h2>
    <p>
      Branding customizes the signer-facing surfaces of eSign Launchpad to look like
      your company: logo, colors, email sender name, legal disclosure, and post-signing
      redirects. Branding is configured once in the portal and applies to every package
      in your tenant, including those you create via the API.
    </p>

    <q-banner class="bg-amber-1 text-amber-10 q-mb-lg" rounded>
      <template v-slot:avatar>
        <q-icon name="warning" color="amber-10" />
      </template>
      <strong>Branding is a portal-only setting.</strong> It requires the
      <code>TenantAdmin</code> role, which no OAuth scope grants. API credentials
      (PATs and OAuth clients) cannot read or modify branding. Sign in to the
      portal and go to <strong>Settings</strong> then <strong>Branding</strong>
      to configure it. Once saved, branding applies to every API-created
      package automatically.
    </q-banner>

    <q-separator class="q-my-lg" />

    <h2>Where branding shows up</h2>
    <p>Tenant branding is rendered in two places:</p>
    <ul>
      <li>
        <strong>Signer notification emails.</strong> Sender name, logo, footer HTML,
        and any legal disclosure text you configure.
      </li>
      <li>
        <strong>The signing ceremony.</strong> Company name, logo, favicon, brand
        colors, completion and decline redirect URLs, and the ESIGN consent
        disclosure.
      </li>
    </ul>
    <p>
      Branding is tenant-wide. There is no per-package override. Every package your
      tenant sends uses the same branding.
    </p>

    <q-separator class="q-my-lg" />

    <h2>Configurable fields</h2>
    <p>The portal's Branding page surfaces these fields:</p>

    <q-markup-table flat bordered dense class="q-mb-md">
      <thead>
        <tr>
          <th class="text-left">Field</th>
          <th class="text-left">What it controls</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Company name</td>
          <td>Display name in emails and the signing ceremony header.</td>
        </tr>
        <tr>
          <td>Logo</td>
          <td>Shown in emails and at the top of the signing ceremony. PNG, JPEG, or SVG, max 2 MB. Transparent background recommended.</td>
        </tr>
        <tr>
          <td>Favicon</td>
          <td>Browser tab icon on signing pages. PNG or ICO, max 100 KB.</td>
        </tr>
        <tr>
          <td>Primary color</td>
          <td>Main brand color for buttons, links, and accents. Hex value, for example <code>#1A73E8</code>.</td>
        </tr>
        <tr>
          <td>Accent color</td>
          <td>Secondary color for highlights. Hex value.</td>
        </tr>
        <tr>
          <td>Button text color</td>
          <td>Text color rendered on primary-colored buttons, for contrast control. Hex value.</td>
        </tr>
        <tr>
          <td>Email sender name</td>
          <td>"From" name shown in signer notification emails.</td>
        </tr>
        <tr>
          <td>Email footer HTML</td>
          <td>Custom HTML appended to the bottom of signer emails, such as your address, disclaimers, or support links.</td>
        </tr>
        <tr>
          <td>Legal disclosure text</td>
          <td>ESIGN or eIDAS consent copy shown to signers before they can sign. Consult your legal team for the correct wording.</td>
        </tr>
        <tr>
          <td>Signing-completed redirect URL</td>
          <td>Where signers land after completing. Leave blank to use the default confirmation page.</td>
        </tr>
        <tr>
          <td>Signing-declined redirect URL</td>
          <td>Where signers land after declining.</td>
        </tr>
        <tr>
          <td>Time zone</td>
          <td>IANA time-zone identifier, for example <code>America/New_York</code>. Used to convert package expiration dates and display timestamps.</td>
        </tr>
        <tr>
          <td>Paper-copy contact</td>
          <td>Contact information included in the ESIGN disclosure so signers know how to request a paper copy.</td>
        </tr>
        <tr>
          <td>Paper-copy fee</td>
          <td>Fee (if any) charged for providing a paper copy, disclosed alongside the contact information.</td>
        </tr>
      </tbody>
    </q-markup-table>

    <q-separator class="q-my-lg" />

    <h2>How to configure branding</h2>
    <ol>
      <li>Sign in to the portal as a user with the <strong>Tenant Admin</strong> role.</li>
      <li>Open <strong>Settings</strong> then <strong>Branding</strong> from the sidebar.</li>
      <li>Upload your logo and favicon. Image URLs are generated automatically and served from our CDN.</li>
      <li>Enter brand colors and company name.</li>
      <li>Set the email sender name, footer HTML, and legal disclosure copy.</li>
      <li>Enter the completion and decline redirect URLs if you want to return signers to your own app after the ceremony.</li>
      <li>Click <strong>Save</strong>. Changes take effect immediately for every subsequent send. In-flight packages continue with whatever branding was active when they were sent.</li>
    </ol>

    <q-banner class="bg-blue-1 text-blue-9 q-mb-lg" rounded>
      <template v-slot:avatar>
        <q-icon name="info" color="blue-9" />
      </template>
      <strong>Test before going live.</strong> After saving branding, send a test
      package to yourself and walk through the signing ceremony end-to-end. Verify the
      logo renders, buttons use your brand color, the email sender name looks right,
      and the redirect URLs behave as expected.
    </q-banner>

    <q-separator class="q-my-lg" />

    <h2>Subscription requirement</h2>
    <p>
      Custom branding is available on paid subscription tiers. Free-tier tenants use
      the default platform branding. The portal shows the upgrade path inline if your
      plan does not include branding.
    </p>

    <q-separator class="q-my-lg" />

    <h2>Best practices</h2>
    <ul>
      <li><strong>Transparent-background logos</strong> render well in both light and dark email clients.</li>
      <li><strong>Match your marketing site</strong> on color values so the signing ceremony feels continuous with the rest of your customer journey.</li>
      <li><strong>Always set completion and decline redirects</strong> when embedding signing into your own app. Without them, signers land on the default confirmation page, breaking the embedded experience.</li>
      <li><strong>Include a paper-copy contact</strong> even if you never expect one to be requested. The ESIGN disclosure requirement is satisfied by the contact information being present.</li>
    </ul>

    <q-separator class="q-my-lg" />

    <h2>Related</h2>
    <ul>
      <li><a href="/Docs/IntegrationGuides?topic=embedded-signing">Embedded Signing</a>. Pair branding with embedded signing for a seamless in-app experience.</li>
      <li><a href="/Docs/IntegrationGuides?topic=authentication">Authentication &amp; Scopes</a>. Why OAuth credentials cannot configure branding, and how scopes work for the rest of the API.</li>
    </ul>

    <section class="dev-section">
      <h2>For developers and enterprise integration</h2>
      <p>
        Branding is not accessible via OAuth credentials. The branding endpoints
        (<code>GET/PUT /api/v1/branding</code> and logo or favicon upload) require the
        <code>TenantAdmin</code> portal role, which no OAuth scope grants. If you
        authenticate with a PAT or an OAuth client-credentials token and call
        these endpoints, you get <code>403 Forbidden</code> with error code
        <code>UNAUTHORIZED</code>.
      </p>
      <p>
        This is intentional. Branding is a tenant-wide configuration concern, not a
        per-integration one. Changes to branding affect every signer email and
        ceremony in your tenant, so the platform gates it behind portal-admin
        authentication to prevent automated tooling from inadvertently rebranding your
        workspace.
      </p>
      <h3>What you <em>can</em> do programmatically</h3>
      <ul>
        <li><strong>Read the current branding at signing time.</strong> The signing ceremony automatically uses whatever branding is configured. No API call needed on your side.</li>
        <li><strong>Set per-signer redirect URLs</strong> that override the tenant-wide defaults. Embedded signers set this via the <code>returnUrl</code> parameter when you generate a signing URL. See <a href="/Docs/IntegrationGuides?topic=embedded-signing">Embedded Signing</a>.</li>
        <li><strong>Customize email subject and message per package</strong> via the <code>emailSubject</code> and <code>emailMessage</code> fields on the package object. See <a href="/Docs/IntegrationGuides?topic=packages">Packages</a>.</li>
      </ul>
      <h3>MCP tools</h3>
      <p>
        No MCP tools are exposed for branding, for the same reason. Branding requires
        <code>TenantAdmin</code> privileges. Configure it once in the portal and every
        API-created package inherits it automatically.
      </p>
    </section>
  </div>
</template>

<script>
export default {
    name: 'GuideBranding'
};
</script>
